// ---------------------------------------------------------------------------
// researchLlm.js
// The fact / evidence layer that runs BEFORE any writing.
//
// One Claude call with Anthropic's server-side web search tool:
//   1. reads the official website (the source of truth for features,
//      pricing and figures),
//   2. searches for REAL human reviews (Reddit, Trustpilot, G2, Capterra,
//      YouTube, forums...),
//   3. returns a structured fact sheet that separates confirmed features,
//      official facts, real user feedback, limitations,
//      competitors and unknowns.
//
// Anti-fabrication: every user-review source must be a URL that actually
// came back from the web search in this call. Anything else is discarded.
// If web search is unavailable, the run continues without user reviews and
// the page says so instead of inventing them.
// ---------------------------------------------------------------------------
import {getAnthropicClient} from './anthropicClient.js';
import {parseLlmJson} from './jsonRepair.js';

const MAX_SEARCHES=10;
const MAX_CONTINUATIONS=5;

// The _20260209 tool versions (dynamic filtering) need Opus/Sonnet 4.6 or
// newer; older models use the basic versions. Web fetch lets Claude read the
// official page directly (it may only fetch URLs present in the
// conversation), so research still works when the page crawl fails.
const isModern=(model)=> /(opus|sonnet)-(4-[6-9]|[5-9])|fable|mythos/i.test(model || '');
const toolsFor=(model,officialUrl,basic=false)=> {
  const modern=!basic && isModern(model);
  const tools=[{
    type: modern ? 'web_search_20260209' : 'web_search_20250305',
    name: 'web_search',
    max_uses: MAX_SEARCHES,
  }];
  if (officialUrl) {
    tools.push({
      type: modern ? 'web_fetch_20260209' : 'web_fetch_20250910',
      name: 'web_fetch',
      max_uses: 3,
    });
  }
  return tools;
};

const RESEARCH_SYSTEM=`You are a careful product researcher preparing a fact sheet for an honest, evidence-based review. You separate what is confirmed from what is merely claimed, and you never invent anything. Return ONLY valid JSON at the end of your work. No markdown, no code fences.`;

const buildResearchPrompt=({product,keyword,officialUrl,pageText})=> `Research the product "${product}" (search phrase: "${keyword}").
${officialUrl ? `Official website (the source of truth for features, pricing, terms and figures): ${officialUrl}${pageText ? '' : '\nThe page text could not be extracted, so read it with web_fetch first.'}` : 'Find the official website first.'}
${pageText ? `\nText already extracted from the official page:\n---\n${pageText.slice(0,4000)}\n---\n` : ''}
STEPS
1. Use the official website for features, pricing, refund/trial terms and key figures. Everything it states is treated as fact.
2. Search for REAL human reviews and discussions. Run several different searches, for example:
   "${product} review", "${product} reviews reddit", "${product} trustpilot", "${product} review youtube",
   "is ${product} legit", "${product} complaints", "${product} vs", "${product} G2 OR Capterra".
   Genuine reviews include: Reddit threads, Trustpilot/G2/Capterra/Product Hunt reviews, YouTube video reviews,
   forum threads (e.g. BlackHatWorld, Warrior Forum) and independent blog reviews written by people who used it.
   A pure sales page that only repeats the vendor's marketing is not a review; an affiliate blogger who
   describes their own use still counts, so note it as affiliate in the point.
3. Identify the product category and the technical questions a careful buyer in that category would ask (e.g. for an SEO/link tool: which platforms links are created on, whether those pages get indexed, follow vs nofollow, whether the buyer owns/controls the properties, whether quoted DA/DR refers to the root domain rather than the created page, what SEO benefit is realistic). Answer them only from sources; otherwise mark them unknown.

RULES
- Never invent reviews, quotes, ratings, prices, statistics, URLs or features.
- user_reviews.sources may ONLY contain pages you actually found with web search. COPY each URL exactly as it appeared in the search results (do not shorten, rewrite or guess it). List up to 10 sources. Paraphrase what each source says; do not fabricate quotes.
- unknowns and limitations are about information that is genuinely missing (e.g. pricing not published, no word on indexing) or real drawbacks; never list doubts about what the official website states.
- Set user_reviews.found to true when you found at least one genuine review; set it to false only if, after several searches, you found none.

Return ONLY this JSON:
{"product_name":"","category":"","primary_use_case":"","official_site":"",
"confirmed_features":[{"feature":"","detail":"","source_url":""}],
"official_facts":[{"fact":"","source_url":""}],
"pricing":{"summary":"","known":true,"source_url":""},
"refund_trial":{"summary":"","source_url":""},
"user_reviews":{"found":false,"summary":"","common_praise":[""],"common_complaints":[""],
  "sources":[{"site":"","url":"","sentiment":"positive|mixed|negative","point":""}]},
"technical_questions":[{"question":"","answer":"","known":true}],
"advantages":[""],"limitations":[""],
"competitors":[{"name":"","difference":""}],
"ideal_user":"","poor_fit_user":"","unknowns":[""],
"final_assessment":""}`;

// Collect every URL that came back from the tools in this conversation.
// Tool results can arrive in several block shapes (search results, fetch
// results, and code-execution output when the newer search tool filters
// results), so every *_tool_result block is walked in full and any URL in
// it is recorded. Citations on Claude's text point at real results too.
// Claude's own written JSON is never a source of "found" URLs.
const URL_IN_TEXT=/https?:\/\/[^\s"'<>()\[\]{}]+/g;
const walkForUrls=(value,urls)=> {
  if (typeof value==='string') {
    for (const match of value.match(URL_IN_TEXT) || []) urls.add(match.replace(/[.,;:!?]+$/,''));
  } else if (Array.isArray(value)) {
    value.forEach((item)=> walkForUrls(item,urls));
  } else if (value && typeof value==='object') {
    Object.values(value).forEach((item)=> walkForUrls(item,urls));
  }
};
const collectSearchUrls=(content,urls)=> {
  for (const block of content || []) {
    if (typeof block?.type==='string' && block.type.endsWith('_tool_result')) walkForUrls(block.content,urls);
    if (block?.type==='text' && Array.isArray(block.citations)) {
      for (const citation of block.citations) if (citation?.url) urls.add(citation.url);
    }
  }
};

// Host without www/m/old/amp prefixes; path lowercased, without query,
// fragment, trailing slash or /amp suffix.
const normaliseUrl=(url)=> {
  try {
    const u=new URL(url);
    const host=u.hostname.toLowerCase().replace(/^(?:www|m|mobile|old|new|amp)\./,'');
    let path=decodeURIComponent(u.pathname).toLowerCase().replace(/\/amp\/?$/,'').replace(/\/+$/,'');
    // YouTube: the video id is the page, not the path.
    if (host==='youtu.be') return {host: 'youtube.com',path: `/watch/${path.slice(1)}`};
    if (host.endsWith('youtube.com') && u.searchParams.get('v')) return {host: 'youtube.com',path: `/watch/${u.searchParams.get('v').toLowerCase()}`};
    // Short paths with a query (forum "viewtopic.php?t=123") keep the query.
    if (path.split('/').filter(Boolean).length <=1 && u.search) path+=`?${[...u.searchParams].sort().map(([k,v])=> `${k}=${v}`).join('&')}`;
    return {host,path};
  } catch { return null; }
};

// A cited review page counts as found if the search returned:
//   - the same page (query strings like ?page=2 ignored), or
//   - a deeper page inside it (the cited page then exists, e.g. a Reddit
//     comment permalink inside the cited thread), or
//   - a thread-level page (4+ path segments) that the cited URL sits inside.
// A listing page (e.g. /r/SEO) never vouches for a thread under it.
const segments=(path)=> path.split('/').filter(Boolean).length;
const pageMatches=(cited,result)=> {
  if (!cited || !result || cited.host!==result.host) return false;
  if (cited.path===result.path) return true;
  if (segments(cited.path) >=2 && result.path.startsWith(`${cited.path}/`)) return true;
  return segments(result.path) >=4 && cited.path.startsWith(`${result.path}/`);
};

const asList=(v)=> (Array.isArray(v) ? v : []).map((x)=> (typeof x==='string' ? x.trim() : x)).filter((x)=> x && (typeof x!=='string' || x.length));

/** Keep the fact sheet well-formed and drop review sources not found by search. */
const sanitiseFactSheet=(raw,searchUrls,product)=> {
  const found=[...searchUrls].map(normaliseUrl).filter(Boolean);
  const reviews=raw.user_reviews || {};
  const cited=asList(reviews.sources).filter((s)=> s && s.url);
  const isFound=(url)=> { const n=normaliseUrl(url); return found.some((f)=> pageMatches(n,f)); };
  const sources=cited.filter((s)=> isFound(s.url)).slice(0,10);
  const droppedUrls=cited.filter((s)=> !isFound(s.url)).map((s)=> s.url);
  return {
    product_name: raw.product_name || product,
    category: raw.category || '',
    primary_use_case: raw.primary_use_case || '',
    official_site: raw.official_site || '',
    confirmed_features: asList(raw.confirmed_features).filter((f)=> f.feature).slice(0,12),
    official_facts: asList(raw.official_facts).filter((f)=> f.fact).slice(0,12),
    pricing: raw.pricing || {summary: '',known: false},
    refund_trial: raw.refund_trial || {summary: ''},
    user_reviews: {
      // Shown only when at least one review page was verified as found.
      found: sources.length > 0,
      summary: sources.length ? (reviews.summary || '') : '',
      common_praise: sources.length ? asList(reviews.common_praise).slice(0,6) : [],
      common_complaints: sources.length ? asList(reviews.common_complaints).slice(0,6) : [],
      sources,
    },
    technical_questions: asList(raw.technical_questions).filter((q)=> q.question).slice(0,8),
    advantages: asList(raw.advantages).slice(0,8),
    limitations: asList(raw.limitations).slice(0,8),
    competitors: asList(raw.competitors).filter((c)=> c.name).slice(0,5),
    ideal_user: raw.ideal_user || '',
    poor_fit_user: raw.poor_fit_user || '',
    unknowns: asList(raw.unknowns).slice(0,8),
    final_assessment: raw.final_assessment || '',
    search_sources: [...searchUrls].slice(0,25),
    dropped_unverified_sources: droppedUrls.length,
    dropped_urls: droppedUrls.slice(0,5),
    claude_reported_found: Boolean(reviews.found),
    cited_count: cited.length,
  };
};

const statusOf=(error)=> error?.status ?? error?.statusCode;

/**
 * Run the research step.
 * @returns {Promise<object|null>} fact sheet, or null if research failed
 */
export const researchProduct=async ({product,keyword,officialUrl,pageText,apiKey,model,proxyUrl,log})=> {
  const {client}=getAnthropicClient(apiKey,proxyUrl,false);
  const searchUrls=new Set();
  let tools=toolsFor(model,officialUrl);
  const messages=[{role: 'user',content: buildResearchPrompt({product,keyword,officialUrl,pageText})}];

  const send=async ()=> client.messages.create({
    model,
    max_tokens: 12000,
    system: RESEARCH_SYSTEM,
    tools,
    messages,
  });

  let response;
  try {
    try {
      response=await send();
    } catch (error) {
      if (statusOf(error)!==400) throw error;
      // Model or org doesn't accept the newer tool versions (or web fetch):
      // retry with the basic versions, then with web search alone.
      try {
        tools=toolsFor(model,officialUrl,true);
        response=await send();
      } catch (retryError) {
        if (statusOf(retryError)!==400 || tools.length===1) throw retryError;
        tools=tools.slice(0,1);
        response=await send();
      }
    }
    collectSearchUrls(response.content,searchUrls);

    // Long research turns can pause; resume by sending the turn back.
    for (let i=0;i < MAX_CONTINUATIONS && response.stop_reason==='pause_turn';i++) {
      messages.push({role: 'assistant',content: response.content});
      response=await send();
      collectSearchUrls(response.content,searchUrls);
    }
  } catch (error) {
    log?.(`⚠ Research step failed (${error.message}). Continuing with the official page only; no user reviews will be shown. (Web search must be enabled for your API organisation in the Anthropic Console.)`);
    return null;
  }

  const text=(response.content || []).filter((b)=> b.type==='text').map((b)=> b.text).join('\n');
  try {
    const {data}=parseLlmJson(text);
    const sheet=sanitiseFactSheet(data,searchUrls,product);
    log?.(`✅ Research: ${sheet.confirmed_features.length} features from the official site, ${sheet.user_reviews.sources.length} real user-review sources (${searchUrls.size} pages searched)`);
    if (sheet.dropped_unverified_sources) {
      log?.(`   ↳ Not shown: ${sheet.dropped_unverified_sources} cited review page(s) that the search did not return, e.g. ${sheet.dropped_urls.slice(0,3).join(', ')}`);
    }
    if (!sheet.user_reviews.found) {
      log?.(sheet.cited_count
        ? `   ↳ Claude cited ${sheet.cited_count} review page(s), but none could be verified against the search results, so no reviews will be shown`
        : '   ↳ Claude found no genuine user reviews in its searches; pages will say reviews were scarce');
    }
    return sheet;
  } catch (error) {
    log?.(`⚠ Research returned unreadable data (${error.message}). Continuing with the official page only.`);
    return null;
  }
};

/** Compact text version of the fact sheet for the writing prompt. */
export const factSheetForPrompt=(sheet)=> (sheet ? JSON.stringify({
  ...sheet,
  search_sources: undefined,
  dropped_unverified_sources: undefined,
  dropped_urls: undefined,
  claude_reported_found: undefined,
  cited_count: undefined,
}).slice(0,12000) : '');
